import { DbRef, SupplyItemStatus, SupplyShipmentStatusName } from '@caresend/types';
import { objectMap, arrayToObj, getShipmentPath, getSupplyItemPath, getSupplyTransferPath, getDraftWaypointPath, decryptDraftWaypoint, getOrderPath, getOfficePath, getProcedurePath, nullishFilter, hasStatus, getValidatedDraftSupplyShipment, getValidatedSupplyShipment, getUnknownErrorMessage, objectFilter, objectReduce } from '@caresend/utils';
import update from 'immutability-helper';
import { getValueOnce, getValuesOnceByID, dbGroupSet } from '../../../database/firebaseConfig/methods.js';
import { toastErrorAndReport, toastError, toastInfo } from '../../../functions/toast/toast.js';
import { hasMutation, customCommit } from '../../helpers.js';
import { initExtendedModule } from '../helpers.js';
import { decryptShipment, maybeAssignPackerToSupplyTransfer, maybeAssignPackerToSupplyShipment } from './helpers.js';

const suppliesState = {
    barcodes: null,
    batchItems: null,
    batchSkus: null,
    completedPacks: [],
    completedShipments: [],
    proceduresOfficeSupplyConfig: {},
    shipments: {},
    skus: null,
    supplyItems: null,
    supplyTransfers: {},
};
const suppliesMutations = {
    'supplies/ADD_COMPLETED_PACKS': (state, supplyTransferID) => {
        state.completedPacks.push(supplyTransferID);
    },
    'supplies/ADD_COMPLETED_SUPPLY_SHIPMENT': (state, supplyShipmentID) => {
        state.completedShipments.push(supplyShipmentID);
    },
    'supplies/SET_SKU': (state, sku) => {
        var _a;
        state.skus = update((_a = state.skus) !== null && _a !== void 0 ? _a : {}, {
            [sku.id]: { $set: sku },
        });
    },
    'supplies/SET_PROCEDURES_OFFICE_SUPPLY_CONFIG': (state, supplyConfigs) => {
        state.proceduresOfficeSupplyConfig = {
            ...state.proceduresOfficeSupplyConfig,
            ...supplyConfigs,
        };
    },
    'supplies/SET_SKUS': (state, skus) => {
        var _a;
        state.skus = update((_a = state.skus) !== null && _a !== void 0 ? _a : {}, {
            $merge: skus !== null && skus !== void 0 ? skus : {},
        });
    },
    'supplies/SET_SHIPMENTS': (state, shipments) => {
        state.shipments = update(state.shipments, {
            $merge: shipments !== null && shipments !== void 0 ? shipments : {},
        });
    },
    'supplies/SET_SUPPLY_ITEMS': (state, supplyItems) => {
        var _a;
        state.supplyItems = update((_a = state.supplyItems) !== null && _a !== void 0 ? _a : {}, {
            $merge: supplyItems !== null && supplyItems !== void 0 ? supplyItems : {},
        });
    },
    'supplies/SET_SUPPLY_TRANSFERS': (state, supplyTransfers) => {
        state.supplyTransfers = update(state.supplyTransfers, {
            $merge: supplyTransfers !== null && supplyTransfers !== void 0 ? supplyTransfers : {},
        });
    },
    'supplies/UPDATE_SHIPMENT': (state, shipmentUpdate) => {
        if (!state.shipments[shipmentUpdate.id]) {
            toastErrorAndReport(`Missing shipment ${shipmentUpdate.id}`);
            return;
        }
        state.shipments = update(state.shipments, {
            [shipmentUpdate.id]: { $merge: shipmentUpdate },
        });
    },
    'supplies/UPDATE_SUPPLY_TRANSFER': (state, supplyTransferUpdate) => {
        if (!state.supplyTransfers[supplyTransferUpdate.id]) {
            toastErrorAndReport(`Missing transfer ${supplyTransferUpdate.id}`);
            return;
        }
        state.supplyTransfers = update(state.supplyTransfers, {
            [supplyTransferUpdate.id]: { $merge: supplyTransferUpdate },
        });
    },
    'supplies/UPDATE_SHIPMENT_BOX_NURSE_SCANS': (state, valuesByShipmentID) => {
        const updatedShipments = objectMap(valuesByShipmentID, (values, id) => {
            var _a, _b, _c;
            return ({
                ...state.shipments[id],
                scanning: {
                    ...(_a = state.shipments[id]) === null || _a === void 0 ? void 0 : _a.scanning,
                    shipmentBoxNurseScans: {
                        ...(_c = (_b = state.shipments[id]) === null || _b === void 0 ? void 0 : _b.scanning) === null || _c === void 0 ? void 0 : _c.shipmentBoxNurseScans,
                        ...values,
                    },
                },
            });
        });
        state.shipments = update(state.shipments, { $merge: updatedShipments });
    },
};
const initSuppliesActions = () => ({
    'supplies/decryptAndSetShipments': async ({ commit }, { shipments }) => {
        const shipmentsArray = Object.values(shipments);
        const decryptedShipmentPromises = shipmentsArray.map((shipment) => decryptShipment(shipment));
        const decryptedShipmentsArray = await Promise.all(decryptedShipmentPromises);
        const decryptedShipments = arrayToObj(decryptedShipmentsArray, 'id');
        commit('supplies/SET_SHIPMENTS', decryptedShipments);
    },
    'supplies/decryptShipmentAndFetchDependencies': async ({ commit, dispatch }, { supplyShipment }) => {
        var _a;
        const boxSupplyItem = await getValueOnce(`${DbRef.SUPPLY_ITEMS}/${(_a = supplyShipment.shipmentBoxSupplyItem) === null || _a === void 0 ? void 0 : _a.id}`);
        if (boxSupplyItem) {
            commit('supplies/SET_SUPPLY_ITEMS', { [boxSupplyItem.id]: boxSupplyItem });
        }
        const supplyTransfer = await getValueOnce(`${DbRef.SUPPLY_TRANSFERS}/${supplyShipment.transferID}`);
        if (supplyTransfer) {
            const supplyTransferByID = arrayToObj([supplyTransfer], 'id');
            commit('supplies/SET_SUPPLY_TRANSFERS', supplyTransferByID);
        }
        const supplyShipmentByID = arrayToObj([supplyShipment], 'id');
        await dispatch('supplies/decryptAndSetShipments', { shipments: supplyShipmentByID });
    },
    'supplies/fetchShipments': async ({ dispatch }, { shipmentIDs }) => {
        const shipmentPaths = shipmentIDs.map((id) => getShipmentPath(id));
        const shipments = await getValuesOnceByID(shipmentPaths);
        await dispatch('supplies/decryptAndSetShipments', { shipments });
    },
    'supplies/fetchSupplyItems': async ({ commit }, { supplyItemIDs }) => {
        const supplyItemPaths = supplyItemIDs.map((id) => getSupplyItemPath(id));
        const supplyItems = await getValuesOnceByID(supplyItemPaths);
        commit('supplies/SET_SUPPLY_ITEMS', supplyItems);
    },
    'supplies/fetchSupplyTransferAndDependencies': async ({ commit, dispatch }, { supplyTransferID, packerID }) => {
        const supplyTransferPath = getSupplyTransferPath(supplyTransferID);
        const supplyTransfer = await getValueOnce(supplyTransferPath);
        if (!supplyTransfer) {
            toastError(`Supply transfer ${supplyTransferID} not found.`);
            return;
        }
        if (packerID) {
            await maybeAssignPackerToSupplyTransfer(supplyTransfer, packerID);
        }
        await dispatch('supplies/fetchSupplyTransferDependencies', {
            supplyTransfer,
            fetchDraftWaypointProcedures: true,
        });
        commit('supplies/SET_SUPPLY_TRANSFERS', { [supplyTransferID]: supplyTransfer });
    },
    // Must use `function` syntax for `this` checks for optional mutations.
    'supplies/fetchSupplyTransferDependencies': async function (context, { supplyTransfer, fetchDraftWaypointProcedures }) {
        var _a, _b;
        const { commit } = context;
        const shipments = {};
        const procedures = {};
        /** By procedureID */
        const taskIDs = {};
        const proceduresOfficeSupplyConfig = {};
        const promises = [];
        const shipmentPromises = Object.keys((_a = supplyTransfer.shipmentIDs) !== null && _a !== void 0 ? _a : {}).map(async (supplyShipmentID) => {
            const encryptedSupplyShipment = await getValueOnce(getShipmentPath(supplyShipmentID));
            if (encryptedSupplyShipment) {
                const supplyShipment = await decryptShipment(encryptedSupplyShipment);
                shipments[supplyShipmentID] = supplyShipment;
            }
        });
        promises.push(...shipmentPromises);
        const draftWaypoint = await getValueOnce(getDraftWaypointPath(supplyTransfer.draftWaypointID));
        try {
            const unencryptedDraftWaypoint = draftWaypoint ? await decryptDraftWaypoint(draftWaypoint) : null;
            if (unencryptedDraftWaypoint) {
                if (hasMutation(this, 'waypoint/SET_DRAFT_WAYPOINTS')) {
                    customCommit(commit, 'waypoint/SET_DRAFT_WAYPOINTS', {
                        [unencryptedDraftWaypoint.id]: unencryptedDraftWaypoint,
                    });
                }
                else if (hasMutation(this, 'draftWaypoints/SET_DRAFT_WAYPOINTS')) {
                    customCommit(commit, 'draftWaypoints/SET_DRAFT_WAYPOINTS', {
                        [unencryptedDraftWaypoint.id]: unencryptedDraftWaypoint,
                    });
                }
                else {
                    console.info('No mutation found to set draftWaypoints.');
                }
                const getProcedureOfficeSupplyConfig = async (procedure) => {
                    const { id: procedureID, orderID, taskID } = procedure;
                    const initializingOfficeID = await getValueOnce(getOrderPath(orderID, 'officeID'));
                    if (initializingOfficeID) {
                        const officeSupplyConfig = await getValueOnce(getOfficePath(initializingOfficeID, `supplyConfig/${taskID}`));
                        if (officeSupplyConfig) {
                            const procedureOfficeSupplyConfig = {
                                procedureID,
                                officeID: initializingOfficeID,
                                supplyConfig: officeSupplyConfig,
                            };
                            proceduresOfficeSupplyConfig[procedureID] = procedureOfficeSupplyConfig;
                        }
                    }
                };
                const procedurePromises = Object.keys((_b = unencryptedDraftWaypoint.procedures) !== null && _b !== void 0 ? _b : {}).map(async (procedureID) => {
                    // Packers do not have permission to read full procedures until they
                    // are assigned to a supplyTransfer. If they are assigned, pass
                    // `fetchDraftWaypointProcedures` as true. Otherwise, they will only
                    // fetch the taskID on the procedure, which they always have permission
                    // to read.
                    if (fetchDraftWaypointProcedures) {
                        const procedure = await getValueOnce(getProcedurePath(procedureID));
                        if (procedure) {
                            procedures[procedureID] = procedure;
                            taskIDs[procedureID] = procedure.taskID;
                            await getProcedureOfficeSupplyConfig(procedure);
                        }
                    }
                    else {
                        const taskID = await getValueOnce(getProcedurePath(procedureID, 'taskID'));
                        if (taskID) {
                            taskIDs[procedureID] = taskID;
                        }
                    }
                });
                promises.push(...procedurePromises);
            }
        }
        catch (error) {
            console.error('Error fetching supply transfer dependencies: ', error);
        }
        await Promise.all(promises);
        commit('supplies/SET_SHIPMENTS', shipments);
        commit('procedures/SET_PROCEDURES', procedures);
        commit('supplies/SET_PROCEDURES_OFFICE_SUPPLY_CONFIG', proceduresOfficeSupplyConfig);
        return taskIDs;
    },
    'supplies/fetchWaypointActionsSuppliesToScan': async ({ commit, rootState }, payload) => {
        var _a;
        const { waypointActionID } = payload;
        try {
            const waypointAction = rootState.waypoint.waypointActions[waypointActionID];
            const procedures = Object.keys((_a = waypointAction === null || waypointAction === void 0 ? void 0 : waypointAction.procedures) !== null && _a !== void 0 ? _a : {}).map((id) => rootState.procedures.procedures[id]).filter(nullishFilter);
            const promises = procedures.map(async (procedure) => {
                let kitSupplyItem = null;
                if (procedure.kitSupplyItem) {
                    kitSupplyItem = await getValueOnce(`${DbRef.SUPPLY_ITEMS}/${procedure.kitSupplyItem.id}`);
                }
                if (procedure.procedureBag) {
                    kitSupplyItem = await getValueOnce(`${DbRef.SUPPLY_ITEMS}/${procedure.procedureBag.id}`);
                }
                if (!kitSupplyItem)
                    return;
                return commit('supplies/SET_SUPPLY_ITEMS', {
                    [kitSupplyItem.id]: kitSupplyItem,
                });
            });
            await Promise.all(promises);
        }
        catch (error) {
            toastError(error);
        }
    },
    'supplies/fetchSupplyShipmentAndDependencies': async ({ dispatch }, { supplyShipmentID, packerID }) => {
        const supplyShipmentPath = getShipmentPath(supplyShipmentID);
        const supplyShipment = await getValueOnce(supplyShipmentPath);
        if (!supplyShipment)
            return;
        if (packerID) {
            await maybeAssignPackerToSupplyShipment(supplyShipment, packerID);
        }
        await dispatch('supplies/decryptShipmentAndFetchDependencies', {
            supplyShipment,
        });
    },
    'supplies/updateSkuActiveState': async ({ commit, state }, payload) => {
        var _a;
        const { skuID, retireReason } = payload;
        const sku = (_a = state.skus) === null || _a === void 0 ? void 0 : _a[skuID];
        if (!sku)
            return;
        try {
            const skuPath = `${DbRef.SKUS}/${skuID}`;
            const updates = {
                [`${skuPath}/active`]: !sku.active,
            };
            if (retireReason) {
                updates[`${skuPath}/retireReason`] = retireReason;
            }
            await dbGroupSet(updates);
            toastInfo('SKU updated');
            commit('supplies/SET_SKU', {
                ...sku,
                active: !sku.active,
                retireReason,
            });
        }
        catch (err) {
            toastError(err);
        }
    },
});
const suppliesGetters = {
    'supplies/getAvailableSupplyItemCount': (state) => (skuID) => {
        var _a, _b;
        const sku = (_a = state.skus) === null || _a === void 0 ? void 0 : _a[skuID];
        if (!sku)
            return 0;
        const availableSupplyItemCount = Object.keys((_b = sku.supplyItems) !== null && _b !== void 0 ? _b : {})
            .filter((supplyItemID) => {
            var _a;
            return hasStatus((_a = state.supplyItems) === null || _a === void 0 ? void 0 : _a[supplyItemID], SupplyItemStatus.ACTIVATED);
        }).length;
        return availableSupplyItemCount;
    },
    'supplies/getTotalSupplyItemCount': (state) => (skuID) => {
        var _a, _b;
        const sku = (_a = state.skus) === null || _a === void 0 ? void 0 : _a[skuID];
        if (!sku)
            return 0;
        const totalSupplyItemCount = Object.keys((_b = sku.supplyItems) !== null && _b !== void 0 ? _b : {}).length;
        return totalSupplyItemCount;
    },
    'supplies/getSupplyAssociatedWithSku': (_state, _getters, rootState) => (supplyID) => {
        var _a;
        const supply = (_a = rootState.variables.variables) === null || _a === void 0 ? void 0 : _a.supplies[supplyID];
        return supply === null || supply === void 0 ? void 0 : supply.name;
    },
    'supplies/getNurseSupplyItems': (state) => (user) => {
        var _a, _b;
        const suppyItems = Object.keys((_b = (_a = user.supplies) === null || _a === void 0 ? void 0 : _a.supplyItems) !== null && _b !== void 0 ? _b : {}).map((id) => { var _a; return (_a = state.supplyItems) === null || _a === void 0 ? void 0 : _a[id]; }).filter(nullishFilter);
        return suppyItems;
    },
    'supplies/getSkuByID': (state) => (id) => { var _a; return (_a = state.skus) === null || _a === void 0 ? void 0 : _a[id]; },
    'supplies/getSupplyItemByID': (state) => (id) => { var _a; return (_a = state.supplyItems) === null || _a === void 0 ? void 0 : _a[id !== null && id !== void 0 ? id : '']; },
    'supplies/getSupplyTransferByID': (state) => (id) => { var _a; return (_a = state.supplyTransfers) === null || _a === void 0 ? void 0 : _a[id]; },
    /**
     * Display an error if a shipment is present, but it is not a valid
     * `SupplyShipment`. To access `Shipment` of any type, use
     * `state.supplies.shipments[id]` instead.
     *
     * Returns data regardless of validity.
     */
    'supplies/getSupplyShipmentByID': (state) => (id) => {
        var _a;
        if (!id)
            return;
        const shipment = (_a = state.shipments) === null || _a === void 0 ? void 0 : _a[id];
        if (!shipment)
            return;
        try {
            return hasStatus(shipment, SupplyShipmentStatusName.DRAFT)
                ? getValidatedDraftSupplyShipment(shipment)
                : getValidatedSupplyShipment(shipment);
        }
        catch (error) {
            const errorMessage = getUnknownErrorMessage(error);
            toastErrorAndReport(`Invalid supply shipment ${id}`, errorMessage);
            return shipment;
        }
    },
    'supplies/getSupplyShipmentsBySupplyTransfer': (state) => (supplyTransfer) => {
        var _a;
        return objectFilter(objectMap((_a = supplyTransfer.shipmentIDs) !== null && _a !== void 0 ? _a : {}, ({ id: shipmentID }) => suppliesGetters['supplies/getSupplyShipmentByID'](state)(shipmentID)), nullishFilter);
    },
    'supplies/getSupplyTransfersByDraftWaypoint': (state) => (draftWaypoint) => {
        var _a;
        return objectFilter(objectMap((_a = draftWaypoint.supplyTransfers) !== null && _a !== void 0 ? _a : {}, ({ id: transferID }) => suppliesGetters['supplies/getSupplyTransferByID'](state)(transferID)), nullishFilter);
    },
    'supplies/getSupplyShipmentsByDraftWaypoint': (state) => (draftWaypoint) => objectReduce(suppliesGetters['supplies/getSupplyTransfersByDraftWaypoint'](state)(draftWaypoint), (prev, [_key, currentValue]) => ({
        ...prev,
        ...suppliesGetters['supplies/getSupplyShipmentsBySupplyTransfer'](state)(currentValue),
    }), {}),
};
const getSuppliesModule = () => ({
    state: suppliesState,
    mutations: suppliesMutations,
    actions: initSuppliesActions(),
    getters: suppliesGetters,
});
const initSuppliesModule = (moduleExtensions) => initExtendedModule(getSuppliesModule(), moduleExtensions);

export { getSuppliesModule, initSuppliesModule };
